import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { NextSupportResistanceLineModel } from "../models/support-resistance-line/next-support-resistance-line.model";
import { IComponent } from "./component.interface";

class NextSupportResistanceSettings {
    public nextSupportHide = false;
    public nextResistanceHide = false;
    public nextSupportXValue: number | null = null;
    public nextSupportPrice: number | null = null;
    public nextResistancePrice: number | null = null;
    public nextResistanceXValue: number | null = null;
    public nextSupportTitle = '';
    public nextResistanceTitle = '';
}

export class NextSupportResistanceLineComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private base: IChartContainer;

    private arrowResistanceElement: HTMLElement;
    private arrowSupportElement: HTMLElement;
    private annotations: Array<NextSupportResistanceLineModel> = [];
    private isRemovingForSwitchingSymbol = false;
    private config = new NextSupportResistanceSettings();

    constructor(base: IChartContainer) {
        this.base = base;
        this.arrowResistanceElement = this.base.element.querySelector('.next-resistance-button.r') as HTMLElement;
        this.arrowSupportElement = this.base.element.querySelector('.next-resistance-button.s') as HTMLElement;
        this.subscribeChartService();
        this.onArrowClick();
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.setArrowPosition();
            this.toggleArrow();
            this.setPinAndPosition();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.setArrowPosition();
            this.toggleArrow();
            this.setPinAndPosition();
        });
        this.base.tiChartSeries?.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                this.setPinAndPosition();
            }, 500);
        });
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        this.removeAnnotations();
        this.annotations = [];

        this.isRemovingForSwitchingSymbol = false;
    }

    public hasActiveAnnotations(): boolean {
        return this.annotations.length > 0 &&
            (this.config.nextSupportXValue !== null || this.config.nextResistanceXValue !== null);
    }

    public getAnnotationPrices(): { supportPrice: number | null, resistancePrice: number | null } | null {
        if (!this.hasActiveAnnotations()) {
            return null;
        }
        return {
            supportPrice: this.config.nextSupportPrice,
            resistancePrice: this.config.nextResistancePrice,
        };
    }

    public get elementMaximumWidth(): number {
        let width = 70;
        if (this.base.tiChartSeries?.chartStyleSettings?.showSmartRiskLevel && !this.base.isExtraSmall()) {
            width = 100;
        }
        return width;
    }

    private setArrowPosition() {
        if (!this.base.tiChartSeries) {
            return;
        }

        if (this.arrowResistanceElement) {
            let { top, left } = this.getArrowTopLeft(this.arrowResistanceElement);

            this.arrowResistanceElement.style.top = top + 'px';
            this.arrowResistanceElement.style.left = left + 'px';
        }

        if (this.arrowSupportElement) {
            let { top, left } = this.getArrowTopLeft(this.arrowSupportElement);

            this.arrowSupportElement.style.top = top + 'px';
            this.arrowSupportElement.style.left = left + 'px';
        }
    }

    private getArrowTopLeft(el: HTMLElement) {
        const xAxis: any = this.base.tiChartSeries.highStockChart?.xAxis[0];
        if (!xAxis) {
            return { top: 0, left: 0 };
        }

        let extraTop = 10 + (this.base.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0);
        let left = xAxis.width + 16;

        if (this.base.isExtraSmall()) {
            extraTop = -10;
            left = xAxis.width - 102;
        }

        let top = 0;
        if (el.classList.contains('l')) {
            top = xAxis.height + extraTop;
        } else {
            const elementHeight = 32;
            top = extraTop + elementHeight;
        }

        return { top, left };
    }

    private toggleArrow() {
        if (!this.base.tiChartSeries) {
            return;
        }

        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis || !this.annotations?.length || (!this.config.nextResistanceHide && !this.config.nextSupportHide)) {
            this.arrowResistanceElement.classList.add('hidden');
            this.arrowSupportElement.classList.add('hidden');
            return;
        }

        this.arrowResistanceElement?.removeClass('l');
        this.arrowSupportElement?.removeClass('l');

        if (!this.config.nextResistanceHide || this.config.nextResistancePrice === null) {
            this.arrowResistanceElement.classList.add('hidden');
        } else {
            this.arrowResistanceElement.setAttribute('title', this.config.nextResistanceTitle);
            this.arrowResistanceElement.classList.remove('disabled');
            this.arrowResistanceElement.classList.remove('hidden');
        }

        if (!this.config.nextSupportHide || this.config.nextSupportPrice === null) {
            this.arrowSupportElement.classList.add('hidden');
        } else {
            this.arrowSupportElement.setAttribute('title', this.config.nextSupportTitle);
            this.arrowSupportElement.classList.remove('disabled');
            this.arrowSupportElement.classList.remove('hidden');
        }

        if (this.config.nextResistancePrice < yAxis.min) {
            this.arrowResistanceElement?.addClass('l');
        }

        if (this.config.nextSupportPrice < yAxis.min) {
            this.arrowSupportElement?.addClass('l');
        }

        if (!this.base.tiChartSeries?.chartStyleSettings?.constantResistanceChartCompressEnabled) {
            const isResistanceTooCloseToEdge = this.validateCloseToEdge(yAxis, this.arrowResistanceElement, this.config.nextResistancePrice ?? 0);
            if (!isResistanceTooCloseToEdge) {
                this.arrowResistanceElement.classList.add('disabled');
            }

            const isSupportTooCloseToEdge= this.validateCloseToEdge(yAxis, this.arrowSupportElement, this.config.nextSupportPrice ?? 0);
            if (!isSupportTooCloseToEdge) {
                this.arrowSupportElement.classList.add('disabled');
            }
        }
    }

    private onArrowClick() {
        this.arrowResistanceElement?.addEventListener('click', () => {
            this.arrowClicked(this.arrowResistanceElement, this.config.nextResistancePrice, this.config.nextResistanceXValue ?? 0);
        });
        this.arrowSupportElement?.addEventListener('click', () => {
            this.arrowClicked(this.arrowSupportElement, this.config.nextSupportPrice, this.config.nextSupportXValue ?? 0);
        });
    }

    private arrowClicked(element: HTMLElement, price: number | null, xValue: number) {
        if (!this.base.tiChartSeries) {
            return;
        }

        if (element.classList.contains('disabled')) {
            return;
        }

        const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0];
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];

        let xTooCloseToEdge = true;
        if (price) {
            const currentXMin = xAxis?.min ?? 0;
            const currentXMax = xAxis?.max ?? 0;
            const currentXRange = currentXMax - currentXMin;
            const xPaddingRange = currentXRange * 0.25;
            const distanceFromLeft = xValue - currentXMin;
            const distanceFromRight = currentXMax - xValue;
            xTooCloseToEdge = distanceFromLeft < xPaddingRange || distanceFromRight < xPaddingRange;
        }

        if (xTooCloseToEdge) {
            this.base.tiChartSeries?.setExtreamToMaxRightSidePadding(false);
        }

        setTimeout(() => {
            if (price !== null) {
                const { isTooCloseToEdge, currentRange, distanceFromBottom } = this.validateCloseToEdge(yAxis, element, price);

                let minValue: number, maxValue: number;
                if (isTooCloseToEdge) {
                    // // Just pan - keep the same range, center around arrowPrice
                    // minValue = this.arrowPrice - (currentRange / 2);
                    // maxValue = this.arrowPrice + (currentRange / 2);

                    const paddingRange = currentRange * 0.20;
                    if (distanceFromBottom < paddingRange) {
                        // Too close to bottom, pan down
                        minValue = price - paddingRange;
                        maxValue = minValue + currentRange;
                    } else {
                        // Too close to top, pan up
                        maxValue = price + paddingRange;
                        minValue = maxValue - currentRange;
                    }
                } else {
                    if (!this.base.tiChartSeries?.chartStyleSettings?.constantResistanceChartCompressEnabled) {
                        return; // No need to expand
                    }

                    // Compress/expand with padding (original logic)
                    const constantResistanceViewPaddingPercent = this.base.tiChartSeries?.chartStyleSettings?.constantResistanceViewPaddingPercent ?? 20;
                    const yAxisPaddingFactor = this.percentToPaddingFactor(constantResistanceViewPaddingPercent);
                    const padding = currentRange * yAxisPaddingFactor;
                    maxValue = price + padding;
                    minValue = price - padding;
                }

                this.base.tiChartSeries.yAxisMoved = true;
                this.base.tiChartSeries.highChartAxisBuilder.manualExpandYAxisScale = true;
                this.base.tiChartSeries.highStockChart?.yAxis?.[0].setExtremes(minValue, maxValue);

                setTimeout(() => {
                    this.base.tiChartSeries?.highChartAxisBuilder.buildAxisComponents();
                }, 50);
            }
        }, 200);
    }

    private validateCloseToEdge(yAxis: any, element: HTMLElement, price: number) {
        if (!price) {
            return { isTooCloseToEdge: false, currentRange: 0, distanceFromBottom: 0 };
        }

        // Use the existing margin method from tiChartSeries
        const margin = this.base.tiChartSeries?.getMarginMinMaxForYAxis();
        if (!margin || !price) {
            return { isTooCloseToEdge: false, currentRange: 0, distanceFromBottom: 0 };
        }

        const currentMin = yAxis?.min ?? 0;
        const currentMax = yAxis?.max ?? 0;
        const currentRange = currentMax - currentMin;

        const threshold = this.chartStyle?.constantResistancePanThreshold ? this.chartStyle.constantResistancePanThreshold / 100 : 0.3;
        const currentMargin = currentRange * threshold;
        const posibleMax = margin.originalMax + currentRange + currentMargin;
        const posibleMin = margin.originalMin - currentRange - currentMargin;

        // Check if too close to edge
        let isTooCloseToEdge = price > posibleMin || price < posibleMax;
        if (element.classList.contains('s')) {
            if (price < posibleMin) {
                isTooCloseToEdge = false;
            }
        } else if (element.classList.contains('r')) {
            if (price > posibleMax) {
                isTooCloseToEdge = false;
            }
        }

        const distanceFromBottom = price - currentMin;

        return { isTooCloseToEdge, currentRange, distanceFromBottom };
    }

    private percentToPaddingFactor(percent: number): number {
        return 1 - (percent / 100);
    }

    private removeAnnotations() {
        for (const item of this.annotations) {
            try {
                item.annotation?.destroy();
                item.annotation = null;
            } catch { }
        }

        this.arrowResistanceElement?.addClass('hidden');
        this.arrowSupportElement?.addClass('hidden');
    }

    private chartStyle: ChartStyleSettingsModel | null = null;
    private subscribeChartService() {
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
            const popupModel = this.chartStyle?.chartSettingsPopupModel;
            const nonChartMode = chartStyle?.nonNormalChartMode();
            if (!this.chartStyle.constantResistanceEnabled || nonChartMode || !popupModel?.displayResistanceAndSupport) {
                this.removeAnnotations();
                this.annotations = [];
            }
        });
        this.base.chartService.subscribe_NextSupportResistanceLinesData((data: Array<NextSupportResistanceLineModel>) => {
            const popupModel = this.chartStyle?.chartSettingsPopupModel;
            const nonChartMode = this.chartStyle?.nonNormalChartMode();
            if (this.isRemovingForSwitchingSymbol || !this.chartStyle?.constantResistanceEnabled || nonChartMode || !popupModel || !popupModel.displayResistanceAndSupport) {
                return;
            }

            this.removeAnnotations();

            this.annotations = data;
            this.setPinAndPosition();
        });
    }

    private setPinAndPosition() {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        const xAxis: any = this.base.tiChartSeries.highStockChart?.xAxis?.[0];

        this.config = new NextSupportResistanceSettings();

        const distancePixelsThreshold = this.base.tiChartSeries?.chartStyleSettings?.constantResistanceDistancePixelsThreshold;
        const hoverOpacity = this.base.tiChartSeries?.chartStyleSettings?.constantHoverOpacity ?? 100;
        const opacity = this.base.tiChartSeries?.chartStyleSettings?.constantResistanceStartingOpacity ?? 100;

        for (const item of this.annotations) {
            if (!item.isSupport) {
                this.config.nextResistanceTitle = 'View Next Resistance at $' + FormatHelpers.numberToStringThousandSeparators(item.price, true);
                this.config.nextResistancePrice = item.price;
            } else {
                this.config.nextSupportTitle = 'View Next Support at $' + FormatHelpers.numberToStringThousandSeparators(item.price, true);
                this.config.nextSupportPrice = item.price;
            }

            const { width, xValue } = this.getLineWidth(item.from);
            if (item.isSupport) {
                this.config.nextSupportXValue = xValue;
            } else {
                this.config.nextResistanceXValue = xValue;
            }

            if (!item || !yAxis ||  width < 90 || item.price < yAxis.min || item.price > yAxis.max) {
                if (item.isSupport) {
                    this.config.nextSupportHide = true;
                } else {
                    this.config.nextResistanceHide = true;
                }

                item.annotation?.destroy();
                item.annotation = null;
                continue;
            }

            const top = yAxis.toPixels(item.price, true) + (this.base.tiChartSeries.highChartBuilder?.getChartSpacing()?.top ?? 0);

            let xPosition = 0;
            if (xAxis) {
                xPosition = xAxis.toPixels(xValue);
            }

            if (!xPosition || xPosition > this.base.tiChartSeries.element.clientWidth) {
                if (item.isSupport) {
                    this.config.nextSupportHide = true;
                } else {
                    this.config.nextResistanceHide = true;
                }

                item.annotation?.destroy();
                item.annotation = null;
                continue;
            }

            const op = opacity / 100;
            const hoverOp = hoverOpacity / 100;


            if (item.annotation) {
                item.annotation.attr({
                    x: xPosition < 0 ? 0 : xPosition,
                    y: top - 8,
                    width: width,
                });

                const badge = item.divElement?.querySelector('.next-support-resistance-annotation-badge') as HTMLElement;
                if (badge) {
                    badge.style.left = `${(width / 2) - (item.isSupport ? 33 : 41)}px`;
                }
            } else {
                const price = FormatHelpers.numberToStringThousandSeparators(item.price, true);
                const timeAsString = item.from ? DateHelpers.format('mm-dd-yyyy', item.from) : '';
                const tooltip = item.isSupport ? 'Support @ $' + price + ' ' + timeAsString : 'Resistance @ $' + price + ' ' + timeAsString;
                const divElement = document.createElement('div');
                divElement.setAttribute('title', tooltip);

                divElement.style.width = '100%';
                divElement.style.height = '16px';
                divElement.style.display = 'flex';
                divElement.style.alignItems = 'center';
                divElement.style.justifyContent = 'center';

                const badgeElement = document.createElement('div');
                badgeElement.className = 'next-support-resistance-annotation-badge';
                badgeElement.innerHTML = item.isSupport ? 'SUPPORT' : 'RESISTANCE';
                badgeElement.setAttribute('style', `
                    position: absolute;
                    left: ${(width / 2) - (item.isSupport ? 33 : 41)}px;
                    background-color: rgba(163, 10, 140, ${op});
                    color: #fff;
                    border-radius: 12px;
                    padding: 2px 6px;
                    font-size: 12px;`);

                divElement.appendChild(badgeElement);


                const lineElement = document.createElement('div');
                lineElement.className = 'next-support-resistance-annotation-line';
                lineElement.setAttribute('style', `
                    background-color: rgba(163, 10, 140, ${op});
                    width: 100%;
                    height: 1px;
                `);

                divElement.appendChild(lineElement);
                const renderer = this.base.tiChartSeries.highStockChart.renderer;
                const foreignObjectElement = renderer.createElement('foreignObject');
                foreignObjectElement.element.appendChild(divElement);

                foreignObjectElement.attr({
                    x: xPosition < 0 ? 0 : xPosition,
                    y: top - 8,
                    width: width,
                    height: 16,
                    zIndex: 5
                });

                foreignObjectElement.element.addEventListener('mouseenter', () => {
                    lineElement.style.backgroundColor = `rgba(163, 10, 140, ${hoverOp})`;
                    badgeElement.style.backgroundColor = `rgba(163, 10, 140, ${hoverOp})`;
                });

                foreignObjectElement.element.addEventListener('mouseleave', () => {
                    lineElement.style.backgroundColor = `rgba(163, 10, 140, ${op})`;
                    badgeElement.style.backgroundColor = `rgba(163, 10, 140, ${op})`;
                });

                foreignObjectElement.add();
                item.divElement = divElement;
                item.annotation = foreignObjectElement;
            }
        }

        // Validate distance between support and resistance
        if (distancePixelsThreshold && this.config.nextSupportPrice !== null && this.config.nextResistancePrice !== null && yAxis) {
            const supportPixelY = yAxis.toPixels(this.config.nextSupportPrice, true);
            const resistancePixelY = yAxis.toPixels(this.config.nextResistancePrice, true);
            const pixelDistance = Math.abs(supportPixelY - resistancePixelY);
            if (pixelDistance < distancePixelsThreshold) {
                // Remove annotations if they're too close together
                for (const item of this.annotations) {
                    item.annotation?.destroy();
                    item.annotation = null;
                }
                // Reset configuration
                this.config.nextSupportHide = true;
                this.config.nextResistanceHide = true;
                this.config.nextSupportPrice = null;
                this.config.nextResistancePrice = null;
                this.config.nextSupportXValue = null;
                this.config.nextResistanceXValue = null;
            }
        }

        this.toggleArrow();
        this.setArrowPosition();
    }

    private getLineWidth(entryTime: Date | null): { width: number, xValue: number | null } {
        let xValue: number | null = null;
        if (entryTime) {
            xValue = this.base.tiChartSeries.getXValueByDateTime(entryTime);
        }

        let extraWidthToReduce = 70;
        const chartExtraSmall = this.base.isExtraSmall();
        if (this.base.tiChartSeries?.chartStyleSettings?.showSmartRiskLevel && !chartExtraSmall) {
            extraWidthToReduce = 100;
        }

        let width = this.base.tiChartSeries.element.clientWidth - extraWidthToReduce;

        const mainChartSerie: any = this.base.tiChartSeries.highStockChart?.series?.[0]
        const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0]

        if (mainChartSerie?.processedXData?.length) {
            // xValue = mainChartSerie.processedXData[mainChartSerie.processedXData.length - 1] - this.base.tiChartSeries.chartStyleSettings.constantResistancePositionBarCount;
            if (mainChartSerie && xAxis && mainChartSerie.processedXData.some((x: number) => x == xValue)) {
                const currentXValueWidth = xAxis.toPixels(xValue) ?? 0;
                let widthToReduce = 35 + extraWidthToReduce;
                width = Math.abs(this.base.tiChartSeries.element.clientWidth - currentXValueWidth - widthToReduce);
            }
        }

        return { width: width, xValue: xValue };
    }
}